import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { getMandatoryReasonTexts } from 'lib/courses';
import { addDays, format } from 'date-fns';
import { toDate, toZonedTime } from 'date-fns-tz';
import { ModalButtonWithForm, TargetSchedule } from 'components/common';

import type { Course, Deadline, MandatoryReasons } from 'shared-types';
import type { UpdateShareUserObject } from './ShareUsers';

interface Props {
  course: Course;
  mandatoryReasons?: MandatoryReasons;
  updateUsers: (updateObject: UpdateShareUserObject) => Promise<void>;
  userCourseSettings: Pick<
    Deadline,
    | 'deadlineDate'
    | 'deadlineDurationUnit'
    | 'deadlineDurationValue'
    | 'deadlineType'
  >;
  userId: number;
}

const ShareUsersTargetSchedule = ({
  course,
  mandatoryReasons,
  updateUsers,
  userCourseSettings,
  userId
}: Props) => {
  const { dateNow: now } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: { timeZone }
  } = useReduxState();

  // the user's own setting can't change anything while the
  // course or a group already makes the course mandatory
  const mandatoryDisabledReasons = mandatoryReasons
    ? getMandatoryReasonTexts({
        mandatoryReasons: { ...mandatoryReasons, isUserMandatory: false },
        messages
      })
    : [];

  const handleSubmit = async ({
    isMandatory,
    ...scheduleValues
  }: typeof initialValues) => {
    try {
      await api.put(`/admin/courses/${course.id}/users/${userId}`, {
        ...(scheduleValues.deadlineType === 'none'
          ? { deadlineType: null }
          : scheduleValues),
        deadlineDate: scheduleValues.deadlineDate
          ? toDate(
              `${format(scheduleValues.deadlineDate, 'y-MM-dd')}T23:59:59`,
              { timeZone }
            )
          : null,
        ...(mandatoryDisabledReasons.length ? {} : { isMandatory })
      });

      await updateUsers({});

      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const initialValues = {
    deadlineDate: userCourseSettings?.deadlineDate
      ? Date.parse(
          toZonedTime(userCourseSettings.deadlineDate, timeZone).toString()
        )
      : Date.parse(addDays(now, 1).toString()),
    deadlineDurationUnit: userCourseSettings?.deadlineDurationUnit || 'days',
    deadlineDurationValue: userCourseSettings?.deadlineDurationValue || 7,
    deadlineType: userCourseSettings?.deadlineType
      ? userCourseSettings.deadlineType
      : 'course-default',
    isMandatory: Boolean(mandatoryReasons?.isMandatory)
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon={['far', 'calendar-alt']}
      buttonIsDropdown={true}
      buttonText={messages.courseMandatoryAndTargetScheduleSet}
      initialValues={initialValues}
      modalActionText={messages.commonSave}
      modalTitle={messages.courseMandatoryAndTargetScheduleSet}
      modalWidth="medium"
      onSubmit={handleSubmit}
      stopPropagation={true}
    >
      {() => (
        <div>
          <TargetSchedule
            courseDefaultHint={messages.courseTargetScheduleDefaultHint}
            settings={course}
            includeCourseDefaultOption={true}
            includeCourseDefaultValue={false}
            includeMandatoryOption={true}
            mandatoryDisabledReasons={mandatoryDisabledReasons}
          />
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default ShareUsersTargetSchedule;
